import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import Alert from '@/Components/Alert';
import {
    PlusIcon, EyeIcon, TrashIcon, QrCodeIcon, ArrowDownTrayIcon,
    MagnifyingGlassIcon, FunnelIcon, XMarkIcon, ChartBarIcon,
    SignalIcon, DocumentIcon
} from '@heroicons/react/24/outline';

export default function Index({ qrCodes, totalQrCodes, totalScans, activeCount, fileCount, flash }) {
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [search, setSearch] = useState('');
    const [type, setType] = useState('');
    const [status, setStatus] = useState('');
    const [scans, setScans] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const applyFilters = (e) => {
        e?.preventDefault();
        const params = {};
        if (search) params.search = search;
        if (type) params.type = type;
        if (status) params.status = status;
        if (scans) params.scans = scans;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        router.get('/qr-codes', params, { preserveState: true });
    };

    const clearFilters = () => {
        setSearch(''); setType(''); setStatus(''); setScans(''); setDateFrom(''); setDateTo('');
        router.get('/qr-codes');
    };

    const hasFilters = search || type || status || scans || dateFrom || dateTo;

    const stats = [
        { label: 'Total QR Codes', value: totalQrCodes, icon: QrCodeIcon, color: 'text-slate-700', bg: 'bg-slate-100' },
        { label: 'Total Scans', value: totalScans, icon: ChartBarIcon, color: 'text-blue-700', bg: 'bg-blue-100' },
        { label: 'Active', value: activeCount, icon: SignalIcon, color: 'text-emerald-900', bg: 'bg-emerald-100' },
        { label: 'File QR Codes', value: fileCount, icon: DocumentIcon, color: 'text-purple-700', bg: 'bg-purple-100' },
    ];

    return (
        <AppLayout>
            <Head title="QR Codes" />
            <PageHeader
                title="QR Codes"
                subtitle="Manage exhibition QR codes"
                actions={
                    <div className="flex gap-2">
                        <Button onClick={() => router.post('/qr-codes-cleanup')} variant="outline" size="sm">
                            <TrashIcon className="w-4 h-4" /> Cleanup Broken
                        </Button>
                        <Button href="/qr-codes/create" size="sm">
                            <PlusIcon className="w-4 h-4" /> Create QR Code
                        </Button>
                    </div>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {stats.map((stat, i) => (
                    <Card key={i}>
                        <CardBody className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                                <stat.icon className={`w-5 h-5 ${stat.color}`} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800">{stat.value ?? 0}</p>
                                <p className="text-xs text-slate-500">{stat.label}</p>
                            </div>
                        </CardBody>
                    </Card>
                ))}
            </div>

            {/* Filters */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="relative sm:col-span-2">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search by title, description, URL..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                                />
                            </div>
                            <FormSelect value={type} onChange={e => setType(e.target.value)}>
                                <option value="">All Types</option>
                                <option value="url">URL</option>
                                <option value="file">File</option>
                            </FormSelect>
                            <FormSelect value={status} onChange={e => setStatus(e.target.value)}>
                                <option value="">All Statuses</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </FormSelect>
                            <FormSelect value={scans} onChange={e => setScans(e.target.value)}>
                                <option value="">Scan Count</option>
                                <option value="none">No scans</option>
                                <option value="low">1-10 scans</option>
                                <option value="medium">11-100 scans</option>
                                <option value="high">100+ scans</option>
                            </FormSelect>
                            <FormInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="From" />
                            <FormInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} placeholder="To" />
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm">
                                <FunnelIcon className="w-4 h-4" /> Apply
                            </Button>
                            {hasFilters && (
                                <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                                    <XMarkIcon className="w-4 h-4" /> Clear
                                </Button>
                            )}
                        </div>
                    </form>
                </CardBody>
            </Card>

            {/* Table */}
            <Card>
                <Table minWidth="800px">
                    <thead>
                        <tr>
                            <Th>Title</Th>
                            <Th>Type</Th>
                            <Th>Slug / URL</Th>
                            <Th>Scans</Th>
                            <Th>Status</Th>
                            <Th>Created</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {qrCodes?.map(qr => (
                            <Tr key={qr.id}>
                                <Td className="font-medium text-slate-800">{qr.title}</Td>
                                <Td>
                                    <Badge variant={qr.type === 'url' ? 'info' : 'purple'}>
                                        {qr.type === 'url' ? 'URL' : 'File'}
                                    </Badge>
                                </Td>
                                <Td className="text-slate-500 text-xs max-w-[200px] truncate">
                                    {qr.type === 'url' ? qr.url : qr.slug}
                                </Td>
                                <Td>
                                    <Badge variant={qr.scan_count > 0 ? 'info' : 'neutral'}>
                                        {qr.scan_count || 0}
                                    </Badge>
                                </Td>
                                <Td>
                                    <Badge variant={qr.is_active ? 'success' : 'neutral'}>
                                        {qr.is_active ? 'Active' : 'Inactive'}
                                    </Badge>
                                </Td>
                                <Td className="text-xs text-slate-500 whitespace-nowrap">
                                    {new Date(qr.created_at).toLocaleDateString()}
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-1">
                                        <Link href={`/qr-codes/${qr.id}`} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                                            <EyeIcon className="w-4 h-4 text-slate-500" />
                                        </Link>
                                        {qr.qr_code_path && qr.qr_code_path !== 'temp' && (
                                            <a href={`/qr-codes/${qr.id}/download`} className="p-1.5 hover:bg-emerald-50 rounded-lg transition-colors">
                                                <ArrowDownTrayIcon className="w-4 h-4 text-emerald-700" />
                                            </a>
                                        )}
                                        <button onClick={() => setDeleteConfirm(qr.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors">
                                            <TrashIcon className="w-4 h-4 text-red-400" />
                                        </button>
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!qrCodes || qrCodes.length === 0) && (
                    <EmptyState
                        icon={QrCodeIcon}
                        title="No QR codes yet"
                        description={hasFilters ? 'Try adjusting your filters.' : 'Generate your first QR code.'}
                        action={
                            <Button href="/qr-codes/create" size="sm">
                                <PlusIcon className="w-4 h-4" /> Create QR Code
                            </Button>
                        }
                    />
                )}
            </Card>

            {/* Delete Confirmation */}
            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete QR Code"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete this QR code? This will also remove the associated files and scan history.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            router.delete(`/qr-codes/${deleteConfirm}`, {
                                onSuccess: () => setDeleteConfirm(null),
                            });
                        }}
                    >
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
